/**
 * FactList — behavioral port of the @smartbox/ui web component.
 *
 * Renders the extractedRecords for a single FileResult:
 *   - Category label (variant="label")
 *   - Extracted value (variant="data" for mono — measured value)
 *   - Confidence percentage (variant="data", color via status tone)
 *   - Source text excerpt (if present)
 *
 * Low-confidence records (<0.70) are highlighted with the inProgress
 * (amber/warning) border — matching the ReconciliationFlag threshold.
 *
 * NEVER imports @smartbox/ui (mobile CLAUDE.md invariant 1).
 *
 * WO-M0 — design-system refactor. All styling via theme tokens + UI primitives.
 */

import { View, StyleSheet } from 'react-native';
import type { ExtractedRecord } from '@beam/api-types';
import { Text } from '../ui';
import { theme } from '../../theme';

const CONFIDENCE_THRESHOLD = 0.70;

interface Props {
  records: ExtractedRecord[];
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}

function FactRow({ record }: { record: ExtractedRecord }) {
  const isLow = record.confidence < CONFIDENCE_THRESHOLD;
  const confidencePct = (record.confidence * 100).toFixed(0) + '%';

  return (
    <View
      style={[styles.row, isLow && styles.rowLow]}
      accessibilityLabel={`${record.category}: ${formatValue(record.value)}, confidence ${confidencePct}`}
    >
      <View style={styles.rowHeader}>
        <Text variant="label" color="secondary" style={styles.categoryText}>
          {record.category}
        </Text>
        {/* Confidence is a measured quantity → data variant */}
        <Text
          variant="data"
          style={{ color: isLow ? theme.status.inProgress.fg : theme.status.paid.fg }}
        >
          {confidencePct}
        </Text>
      </View>

      {/* Extracted value is a measured/data value → data variant */}
      <Text variant="data" numberOfLines={4}>
        {formatValue(record.value)}
      </Text>

      {record.sourceText ? (
        <Text variant="small" color="secondary">
          Source: {record.sourceText}
        </Text>
      ) : null}
    </View>
  );
}

export function FactList({ records }: Props) {
  if (records.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Text variant="small" color="secondary">No facts extracted from this file.</Text>
      </View>
    );
  }

  return (
    <View style={styles.list} accessibilityRole="list">
      {records.map((record, index) => (
        <FactRow key={`${record.fileId}-${record.category}-${index}`} record={record} />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  list: {
    gap: theme.spacing.sm,
  },
  emptyContainer: {
    paddingVertical: theme.spacing.md,
    alignItems: 'center',
  },
  row: {
    backgroundColor: theme.color.bg.app,
    borderRadius: theme.radius.sm,
    padding: theme.spacing.md,
    gap: theme.spacing.xs,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.default,
  },
  rowLow: {
    borderColor: theme.status.inProgress.fg,
    backgroundColor: theme.status.inProgress.bg,
  },
  rowHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.xs,
  },
  categoryText: {
    flex: 1,
  },
});
